import { useLocation } from "gemi/client";
import { type ReactNode, useEffect, useState } from "react";
import { AuthModalProvider } from "./AuthModalV2";
import { ConsentedMetaPixel } from "../components/ConsentedMetaPixel";
import { META_PIXEL_ID_CLIENT } from "../utils/consentAnalytics";
import { CookieConsentBannerV2 } from "./CookieConsentBannerV2";
import { interstitialBootScript } from "./interstitialBoot";
import MobileAppInterstitial from "./MobileAppInterstitial";
import { font } from "./_shared";
import { SiteFooterV2 } from "./SiteFooterV2";
import { SiteNavV2 } from "./SiteNavV2";
import { StudioCTAV2 } from "./StudioCTAV2";

// Deterministic string; compute once at module load, not per render.
const INTERSTITIAL_BOOT = interstitialBootScript();

/**
 * Chrome for the marketing site at the root (home, pricing, about, contact, and
 * the legal/support pages). Loads Onest/Geist/Geist Mono from Google Fonts
 * (React 19 hoists these <link> tags into <head>).
 */
export default function LayoutV2({ children }: { children: ReactNode }) {
  // The interstitial lives on the landing root only. It sits in the LAYOUT (not the
  // lazily-loaded Home view) on purpose: a view chunk's Suspense boundary blanks to
  // its null fallback during hydration on a slow network (see the min-h-screen note
  // below), which would drop the overlay while <body> stays scroll-locked and flash
  // the landing. The layout renders through that window. Each LayoutV2 path is its
  // own cache entry, so gating on pathname keeps "/" device-agnostic and cacheable.
  const { pathname } = useLocation();
  const isHome = pathname === "/";
  // The boot script is emitted in the server-rendered HTML only. It matches on the
  // cold-load client render (so hydration does not mismatch), then this flips and
  // React drops the already-executed node. A client-side SPA hop to "/" therefore
  // never re-runs it, so the interstitial is a cold-entry nudge, not an in-session
  // interruption. The overlay itself still mounts (hidden) so its cleanup can clear
  // any scroll lock when navigating away.
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => setHydrated(true), []);
  return (
    <>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link
        rel="preconnect"
        href="https://fonts.gstatic.com"
        crossOrigin="anonymous"
      />
      <link
        href="https://fonts.googleapis.com/css2?family=Onest:wght@400;500;600;700&family=Geist:wght@300;400;500;600&family=Geist+Mono:wght@300;400;500&display=swap"
        rel="stylesheet"
      />
      <div
        className="relative isolate w-full overflow-x-clip bg-white text-[#343139]"
        style={font.body}
      >
        {/* Hero backdrop sits in the LCP region, so it stays eager (never lazy).
            Decorative (62% opacity + gradient mask), so a downscaled WebP holds up. */}
        <img
          src="/v2/hero-dots.webp"
          alt=""
          aria-hidden
          className="pointer-events-none absolute left-0 top-0 -z-10 h-[480px] w-full object-cover opacity-[0.62] lg:h-[560px]"
          style={{
            WebkitMaskImage:
              "linear-gradient(180deg,#000 64%,transparent 100%)",
            maskImage: "linear-gradient(180deg,#000 64%,transparent 100%)",
          }}
        />
        <AuthModalProvider>
          {isHome && (
            <>
              <MobileAppInterstitial />
              {!hydrated && (
                <script
                  dangerouslySetInnerHTML={{ __html: INTERSTITIAL_BOOT }}
                />
              )}
            </>
          )}
          <SiteNavV2 />
          {/* Reserve at least a viewport of height for the page body. The view
              is a lazily-loaded chunk, so on a slow network its Suspense
              boundary is still on the (null) fallback for a beat during
              hydration and the body collapses to zero height — which would pull
              StudioCTAV2/SiteFooterV2 up into view and then drop them back once
              the chunk arrives (a large layout shift). Holding a viewport here
              keeps them below the fold so that settling happens off-screen.
              Root cause is framework-level (view chunks aren't preloaded):
              nstfkc/gemi#352. Mirrors the guard in LandingLayout. */}
          <div className="min-h-screen">{children}</div>
          <StudioCTAV2 />
          <SiteFooterV2 />
        </AuthModalProvider>
      </div>
      {/* Funnel surface: load the Meta Pixel here (never app-wide) once consent is granted, so a
          returning consented visitor gets PageView even though the banner is hidden. */}
      <ConsentedMetaPixel pixelId={META_PIXEL_ID_CLIENT} />
      <CookieConsentBannerV2 pixelId={META_PIXEL_ID_CLIENT} />
    </>
  );
}
